import { MutableRefObject } from "react";
import styled, { css } from "styled-components";
import { selectedTint } from "features/onboarding/common/onboardingUi";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens";
import { Check } from "lucide-react";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import useMedia from "utils/hooks/useMedia";
import { PHONE } from "features/onboarding/guided/walkthrough/WalkthroughLayout";

type StepState = "done" | "active" | "todo";

interface Step {
  title: string;
  /** Opens under the title while the step is active. */
  description?: string;
}

interface WalkthroughStepsProps {
  steps: (Step | string)[];
  /** The active step's bar: the illustration moves it along as the step plays. */
  progressRef?: MutableRefObject<HTMLElement | null>;
  /** The example's event: step `stage` is active, earlier ones are done. */
  stage: number;
  onSelect: (stage: number) => void;
}

/**
 * The story of the example as a vertical timeline: the active step in ink
 * with an accent dot, done steps muted with a check, upcoming steps muted.
 * Each step jumps the example to its event.
 */
const WalkthroughSteps = ({
  steps: input,
  progressRef,
  stage,
  onSelect,
}: WalkthroughStepsProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.ONBOARDING);
  const phone = useMedia([`(max-width: ${PHONE}px)`], [true], false);
  const steps = input.map(
    (step): Step => (typeof step === "string" ? { title: step } : step)
  );
  // Phones: the story sits right under the example as a pager: one segment
  // per event (the active one fills as it plays), then the active step's
  // words. A segment jumps the example to its event.
  if (phone && steps.some((step) => step.description)) {
    const current = Math.min(Math.max(stage, 1), steps.length);
    const active = steps[current - 1];
    return (
      <Pager aria-label={t("intent.how_it_works")}>
        <Segments>
          {steps.map((step, index) => {
            const event = index + 1;
            const state: StepState =
              stage > event ? "done" : stage === event ? "active" : "todo";
            return (
              <Segment
                key={step.title}
                type="button"
                $state={state}
                aria-label={step.title}
                aria-current={state === "active" ? "step" : undefined}
                onClick={() => onSelect(event)}
              >
                <span>
                  {state === "active" && (
                    <i
                      key={stage}
                      ref={(element) => {
                        if (progressRef) progressRef.current = element;
                      }}
                    />
                  )}
                </span>
              </Segment>
            );
          })}
        </Segments>
        <PagerCount>
          {t("intent.step_of", { n: current, total: steps.length })}
        </PagerCount>
        {/* Every step's words share one cell, so the pager keeps the
            tallest one's height and nothing under it moves between steps. */}
        <PagerWords>
          {steps.map((step) => (
            <div
              key={step.title}
              aria-hidden={step !== active || undefined}
              data-active={step === active || undefined}
            >
              <PagerTitle>{step.title}</PagerTitle>
              {step.description && <PagerText>{step.description}</PagerText>}
            </div>
          ))}
        </PagerWords>
      </Pager>
    );
  }
  // Steps with a description are the bot illustrations': titles, with the
  // active one opened. Plain steps keep the compact list.
  if (steps.some((step) => step.description))
    return (
      <Wrapper>
        <Titled aria-label={t("intent.how_it_works")}>
          {steps.map((step, index) => {
            const event = index + 1;
            const state: StepState =
              stage > event ? "done" : stage === event ? "active" : "todo";
            return (
              <Titled.Item key={step.title} $state={state}>
                <Row
                  type="button"
                  $state={state}
                  aria-current={state === "active" ? "step" : undefined}
                  aria-expanded={state === "active"}
                  onClick={() => onSelect(event)}
                >
                  <Badge $state={state} aria-hidden>
                    {state === "done" ? (
                      <Check size={12} strokeWidth={3} />
                    ) : (
                      event
                    )}
                  </Badge>
                  <RowTitle>{step.title}</RowTitle>
                </Row>
                <Detail $open={state === "active"}>
                  <div>
                    <p>{step.description}</p>
                  </div>
                </Detail>
                {state === "active" && (
                  <Bar aria-hidden>
                    <i
                      key={stage}
                      ref={(element) => {
                        if (progressRef) progressRef.current = element;
                      }}
                    />
                  </Bar>
                )}
              </Titled.Item>
            );
          })}
        </Titled>
      </Wrapper>
    );
  return (
    <Wrapper>
      <Heading>{t("intent.how_it_works")}</Heading>
      <List>
        {steps.map(({ title: text }, index) => {
          const event = index + 1;
          const state: StepState =
            stage > event ? "done" : stage === event ? "active" : "todo";
          return (
            <Item key={text}>
              <StepButton
                type="button"
                $state={state}
                aria-current={state === "active" ? "step" : undefined}
                onClick={() => onSelect(event)}
              >
                <Dot $state={state} aria-hidden>
                  {state === "done" && <Check size={10} strokeWidth={3} />}
                </Dot>
                <span>{text}</span>
              </StepButton>
            </Item>
          );
        })}
      </List>
    </Wrapper>
  );
};

export default WalkthroughSteps;

const Wrapper = styled.div`
  min-width: 0;
`;
const Heading = styled.h2`
  margin: 0 0 8px;
  ${newLookType.micro};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const List = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
`;
const Item = styled.li`
  position: relative;
  /* The timeline: a hairline from one dot to the next. */
  &:not(:last-child)::before {
    content: "";
    position: absolute;
    left: 7.5px;
    top: 28px;
    bottom: -6px;
    width: 1px;
    background: ${(p) => p.theme.colors.cardBorderColor};
  }
`;
const StepButton = styled.button<{ $state: StepState }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 8px 0;
  border: none;
  background: none;
  text-align: left;
  font: inherit;
  ${newLookType.body};
  cursor: pointer;
  color: ${(p) =>
    p.$state === "active"
      ? p.theme.colors.titleColor
      : p.theme.colors.secondaryText};
  /* One weight for every state: a bolder active step re-wraps its text and
     shifts the whole layout as the steps advance. Ink colour and the ring
     mark the active step. */
  font-weight: ${(p) => p.theme.typography.fontWeights.NORMAL};
  transition: color 150ms ease;
  &:hover {
    color: ${(p) => p.theme.colors.titleColor};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 2px;
    border-radius: 6px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
const Dot = styled.span<{ $state: StepState }>`
  flex: none;
  display: inline-grid;
  place-items: center;
  width: 16px;
  height: 16px;
  margin-top: 2px;
  box-sizing: border-box;
  border-radius: 50%;
  ${(p) =>
    p.$state === "active"
      ? css`
          border: 4px solid ${p.theme.colors.primaryBlue};
          background: ${p.theme.colors.cardBgColor};
        `
      : p.$state === "done"
      ? css`
          border: 1px solid ${p.theme.colors.cardBorderColor};
          background: ${p.theme.colors.cardBgColor};
          color: ${p.theme.colors.secondaryText};
        `
      : css`
          border: 1.5px solid ${p.theme.colors.lightBorderGrey};
          background: ${p.theme.colors.cardBgColor};
        `}
`;

/* ------------------------------------------------------------------------ */
/* Phone pager                                                               */
/* ------------------------------------------------------------------------ */

const Pager = styled.section`
  min-width: 0;
`;
const Segments = styled.div`
  display: flex;
  gap: 4px;
  margin: -10px 0 0;
`;
const Segment = styled.button<{ $state: StepState }>`
  flex: 1;
  min-width: 0;
  padding: 10px 0;
  border: none;
  background: none;
  cursor: pointer;
  > span {
    display: block;
    height: 3px;
    border-radius: 2px;
    overflow: hidden;
    background: ${(p) =>
      p.$state === "done"
        ? p.theme.colors.primaryBlue
        : getNewLookTokens(p.theme).hairline};
  }
  i {
    display: block;
    height: 100%;
    background: ${(p) => p.theme.colors.primaryBlue};
    transform-origin: left center;
    transform: scaleX(0);
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 2px;
    border-radius: 4px;
  }
  @media (prefers-reduced-motion: reduce) {
    i {
      transform: none !important;
    }
  }
`;
const PagerCount = styled.div`
  margin-top: 2px;
  ${newLookType.micro};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const PagerWords = styled.div`
  display: grid;
  > div {
    grid-area: 1 / 1;
    visibility: hidden;
  }
  > div[data-active] {
    visibility: visible;
  }
`;
const PagerTitle = styled.h2`
  margin: 6px 0 0;
  font-size: 16px;
  line-height: 22px;
  font-weight: 600;
  color: ${(p) => p.theme.colors.titleColor};
`;
const PagerText = styled.p`
  margin: 4px 0 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${(p) => p.theme.colors.secondaryText};
`;

/* ------------------------------------------------------------------------ */
/* Titled steps                                                              */
/* ------------------------------------------------------------------------ */

const TitledList = styled.ol`
  display: grid;
  gap: 2px;
  margin: 0;
  padding: 0;
  list-style: none;
`;
const TitledItem = styled.li<{ $state: StepState }>`
  position: relative;
  overflow: hidden;
  padding: 10px 12px;
  border-radius: 8px;
  background: ${(p) =>
    p.$state === "active" ? selectedTint(p.theme) : "transparent"};
  transition: background-color 200ms ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
const Titled = Object.assign(TitledList, { Item: TitledItem });
const Row = styled.button<{ $state: StepState }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  text-align: left;
  font: inherit;
  cursor: pointer;
  color: ${(p) =>
    p.$state === "todo"
      ? p.theme.colors.secondaryText
      : p.theme.colors.titleColor};
  &:hover {
    color: ${(p) => p.theme.colors.titleColor};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 4px;
    border-radius: 6px;
  }
`;
const Badge = styled.span<{ $state: StepState }>`
  flex: none;
  display: inline-grid;
  place-items: center;
  width: 22px;
  height: 22px;
  box-sizing: border-box;
  border-radius: 50%;
  font-size: 11.5px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  ${(p) =>
    p.$state === "active"
      ? css`
          background: ${p.theme.colors.primaryBlue};
          border: 1px solid ${p.theme.colors.primaryBlue};
          color: ${p.theme.colors.buttonText};
        `
      : p.$state === "done"
      ? css`
          background: ${p.theme.colors.cardBgColor};
          border: 1px solid ${p.theme.colors.primaryBlue};
          color: ${p.theme.colors.primaryBlue};
        `
      : css`
          background: transparent;
          border: 1px solid ${p.theme.colors.lightBorderGrey};
          color: ${p.theme.colors.secondaryText};
        `}
`;
const RowTitle = styled.span`
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
`;
// Opens smoothly: the grid row grows from 0 to its content.
const Detail = styled.div<{ $open: boolean }>`
  display: grid;
  grid-template-rows: ${(p) => (p.$open ? "1fr" : "0fr")};
  transition: grid-template-rows 250ms ease;
  > div {
    min-height: 0;
    overflow: hidden;
  }
  p {
    margin: 0;
    opacity: ${(p) => (p.$open ? 1 : 0)};
    transition: opacity 200ms ease ${(p) => (p.$open ? "120ms" : "0ms")};
    padding: 6px 0 12px 34px;
    font-size: 13.5px;
    line-height: 1.5;
    color: ${(p) => p.theme.colors.secondaryText};
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
// Time until the next event: a thin bar inside the panel's bottom edge.
const Bar = styled.span`
  position: absolute;
  left: 12px;
  right: 12px;
  bottom: 6px;
  height: 2px;
  border-radius: 1px;
  overflow: hidden;
  background: ${(p) => getNewLookTokens(p.theme).hairline};
  i {
    display: block;
    height: 100%;
    background: ${(p) => p.theme.colors.primaryBlue};
    transform-origin: left center;
    transform: scaleX(0);
  }
  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;
